import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Image, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import type { Tables } from '../lib/types';
import { ITALIAN_PROVINCES } from '../constants/cities';
import RangeSlider from './RangeSlider';

interface NewEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (eventData: any) => void;
  initialData?: Tables<'events'>;
  allProfiles?: any[];
}

const NewEventModal: React.FC<NewEventModalProps> = ({ isOpen, onClose, onSubmit, initialData, allProfiles = [] }) => {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [eventData, setEventData] = useState({
    name: '',
    date: '',
    time: '',
    location: '',
    maxParticipants: '',
    menPrice: '',
    womenPrice: '',
    city: user?.role === 'admin' ? 'ROMA' : user?.city || 'ROMA',
    collaboratorId: '',
  });
  const [ageRange, setAgeRange] = useState<[number, number]>([28, 45]);
  const [locations, setLocations] = useState<Tables<'locations'>[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const parseAgeRange = (rangeStr: string): [number, number] => {
    if (!rangeStr) return [28, 45];
    if (rangeStr.startsWith('over-')) {
      const minAge = parseInt(rangeStr.replace('over-', ''), 10);
      return [minAge + 1, 120];
    }
    const parts = rangeStr.split('-');
    if (parts.length === 2) {
      const min = parseInt(parts[0], 10);
      const max = parseInt(parts[1], 10);
      if (!isNaN(min) && !isNaN(max)) return [min, max];
    }
    return [28, 45];
  };

  useEffect(() => {
    if (!isOpen) return;

    if (initialData) {
      const city = initialData.city || (user?.role === 'admin' ? 'ROMA' : user?.city || 'ROMA');
      const locationName = initialData.location || '';
      setEventData({
        name: initialData.name || '',
        date: initialData.date || '',
        time: initialData.time || '',
        location: locationName,
        maxParticipants: initialData.max_participants?.toString() || '',
        menPrice: initialData.men_price?.toString() || '',
        womenPrice: initialData.women_price?.toString() || '',
        city,
        collaboratorId: (initialData as any).collaborator_id || '',
      });
      setAgeRange(parseAgeRange(initialData.age_range || ''));
      loadLocations(city, locationName);
      setImagePreview((initialData as any).image_url || null);
      setSelectedImage(null);
    } else {
      const city = user?.role === 'admin' ? 'ROMA' : user?.city || 'ROMA';
      setEventData({
        name: 'SPEED DATE',
        date: '',
        time: '',
        location: '',
        maxParticipants: '',
        menPrice: '',
        womenPrice: '',
        city,
        collaboratorId: '',
      });
      setAgeRange([28, 45]);
      loadLocations(city);
      setImagePreview(null);
      setSelectedImage(null);
    }
  }, [initialData, isOpen]);

  const loadLocations = async (selectedCity: string, preserveLocation?: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('status', 'active')
        .eq('city', selectedCity)
        .order('name');

      if (error) {
        throw error;
      }

      setLocations(data || []);

      const locationToCheck = preserveLocation ?? eventData.location;
      if (data && locationToCheck && !data.find(loc => loc.name === locationToCheck)) {
        setEventData(prev => ({ ...prev, location: '' }));
      }
    } catch (error) {
      console.error('Error loading locations:', error);
      alert('Errore nel caricamento delle location');
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = (field: 'menPrice' | 'womenPrice', value: string) => {
    let cleanValue = value.replace(/[^\d.]/g, '');

    const decimalPoints = cleanValue.match(/\./g)?.length || 0;
    if (decimalPoints > 1) {
      const parts = cleanValue.split('.');
      cleanValue = parts[0] + '.' + parts.slice(1).join('');
    }

    if (cleanValue.includes('.')) {
      const [whole, decimal] = cleanValue.split('.');
      cleanValue = whole + '.' + decimal.slice(0, 2);
    }

    setEventData(prev => ({
      ...prev,
      [field]: cleanValue
    }));
  };

  const handleCityChange = (city: string) => {
    setEventData(prev => ({
      ...prev,
      city,
      location: '',
    }));
    loadLocations(city);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSelectedImage(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!selectedImage) return imagePreview;

    setUploading(true);
    try {
      const fileExt = selectedImage.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('event-images')
        .upload(fileName, selectedImage, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('event-images')
        .getPublicUrl(fileName);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setUploading(true);
      const imageUrl = await uploadImage();
      onSubmit({
        ...eventData,
        ageRange: `${ageRange[0]}-${ageRange[1]}`,
        imageUrl,
      });
      onClose();
    } catch (error) {
      console.error('Error submitting event:', error);
      alert('Errore nel caricamento dell\'immagine');
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md md:max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">
            {initialData ? 'Modifica Evento' : 'Nuovo Evento'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nome Evento</label>
              <input
                type="text"
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500 uppercase"
                value={eventData.name}
                onChange={(e) => setEventData({ ...eventData, name: e.target.value.toUpperCase() })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fascia d'Età: {ageRange[0]} - {ageRange[1]} anni
              </label>
              <div className="mt-6 px-1">
                <RangeSlider
                  min={18}
                  max={120}
                  value={ageRange}
                  onChange={setAgeRange}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Data</label>
              <input
                type="date"
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                value={eventData.date}
                onChange={(e) => setEventData({ ...eventData, date: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Ora</label>
              <input
                type="time"
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                value={eventData.time}
                onChange={(e) => setEventData({ ...eventData, time: e.target.value })}
              />
            </div>

            {user?.role === 'admin' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Città</label>
                  <select
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                    value={eventData.city}
                    onChange={(e) => handleCityChange(e.target.value)}
                  >
                    {ITALIAN_PROVINCES.map((city) => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Assegna Collaboratore</label>
                  <select
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                    value={eventData.collaboratorId}
                    onChange={(e) => setEventData({ ...eventData, collaboratorId: e.target.value })}
                  >
                    <option value="">Nessun collaboratore</option>
                    {allProfiles.map((profile) => (
                      <option key={profile.id} value={profile.id}>
                        {profile.name} ({profile.email})
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700">Location</label>
              <select
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                value={eventData.location}
                onChange={(e) => setEventData({ ...eventData, location: e.target.value })}
              >
                <option value="">Seleziona una location</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.name}>
                    {location.name} (Capacità: {location.capacity})
                  </option>
                ))}
              </select>
              {locations.length === 0 && (
                <p className="mt-1 text-sm text-rose-500">
                  Nessuna location disponibile per {eventData.city}. Aggiungi prima una location dalla sezione "Location".
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Numero Massimo Partecipanti</label>
              <input
                type="number"
                required
                min="2"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                value={eventData.maxParticipants}
                onChange={(e) => setEventData({ ...eventData, maxParticipants: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Prezzo Uomini</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">€</span>
                </div>
                <input
                  type="text"
                  required
                  className="pl-7 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                  value={eventData.menPrice}
                  onChange={(e) => handlePriceChange('menPrice', e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Prezzo Donne</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">€</span>
                </div>
                <input
                  type="text"
                  required
                  className="pl-7 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                  value={eventData.womenPrice}
                  onChange={(e) => handlePriceChange('womenPrice', e.target.value)}
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Immagine Evento</label>
              {imagePreview ? (
                <div className="relative rounded-lg overflow-hidden border border-gray-200">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-40 object-cover"
                  />
                  <div className="absolute top-2 right-2 flex gap-1">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="bg-white/90 hover:bg-white text-gray-700 p-1.5 rounded-lg shadow-sm transition-colors"
                      title="Cambia immagine"
                    >
                      <Image className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="bg-white/90 hover:bg-white text-red-600 p-1.5 rounded-lg shadow-sm transition-colors"
                      title="Rimuovi immagine"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-rose-400 hover:bg-rose-50/50 transition-colors"
                >
                  <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500">Clicca per caricare un'immagine</p>
                  <p className="text-xs text-gray-400 mt-1">JPG, PNG, WebP</p>
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Annulla
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-rose-600 text-white rounded-md hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={locations.length === 0 || uploading}
            >
              {uploading ? 'Caricamento...' : initialData ? 'Salva Modifiche' : 'Crea Evento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewEventModal;
